import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, getDocs, query, where, onSnapshot } from "firebase/firestore";
import { storage, ref, uploadBytes, getDownloadURL } from './firebaseUtils.js';
import Spinner from './Spinner.js';
import { ArrowLeft, Edit2, Save, Printer, CheckCircle, FileText, User, Info, Trash2, Search, Upload, Loader2, FileCheck, ExternalLink, Paperclip, Clock, X, ChevronDown } from 'lucide-react';
import useClickOutside from './hooks/useClickOutside.js';
import HorizontalTimeline from './components/HorizontalTimeline.js';

const getFileNameFromUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split('/');
    return pathSegments[pathSegments.length - 1];
  } catch (error) {
    return url;
  }
};

const formatAddress = (addr) => {
  if (!addr) return '';
  return `${addr.address || ""}, ${addr.city || ""}, ${addr.state || ""} - ${addr.pincode || ""}`;
};

const DCDetail = ({ db, user }) => {
  const { id } = useParams(); // MOC ID
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploading, setUploading] = useState({ invoice: false, eway: false, einvoice: false });
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [firms, setFirms] = useState([]);
  const [moc, setMoc] = useState(null);
  const [order, setOrder] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [bomDatabase, setBomDatabase] = useState([]);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useClickOutside(() => setShowSuggestions(false));
  const moreDropdownRef = useClickOutside(() => setShowMoreDropdown(false));
  const printRef = useRef();

  const [dcData, setDcData] = useState({
    firmId: '',
    challanNo: '',
    dispatchDate: new Date().toISOString().split('T')[0],
    deliveryAgent: '',
    deliveryMode: 'Truck',
    coordinator: '',
    dispatchedThrough: '',
    poNumber: '',
    poDate: '',
    irnNo: '',
    billToCustomer: '',
    shipToCustomer: '',
    billToAddress: '',
    shipToAddress: '',
    billToGstin: '',
    salesperson: '',
    orderBy: '',
    orderByContact: '',
    sitePerson: '',
    sitePersonContact: '',
    billToEmail: '',
    shipToEmail: '',
    itemCode: '',
    towerHeight: '',
    totalMeter: '',
    receivedByName: '',
    receivedByContact: '',
    handoverDateTime: '',
    vehicleType: '',
    taxInvoiceUrl: '',
    ewayBillUrl: '',
    einvoiceUrl: '',
    isDCConfirmed: false,
    components: []
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const firmSnap = await getDocs(collection(db, "firms"));
        setFirms(firmSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const bomSnap = await getDocs(collection(db, "bom_products"));
        setBomDatabase(bomSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const mocRef = doc(db, "dispatches", id);
        const mocSnap = await getDoc(mocRef);
        
        if (mocSnap.exists()) {
          const mData = mocSnap.data();
          setMoc({ id: mocSnap.id, ...mData });

          let orderData = null;
          let customerData = null;

          if (mData.orderId) {
            const orderSnap = await getDoc(doc(db, "orders", mData.orderId));
            if (orderSnap.exists()) {
                orderData = { id: orderSnap.id, ...orderSnap.data() };
                setOrder(orderData);
            }
          }

          if (mData.customerID) {
            const custSnap = await getDoc(doc(db, "customers", mData.customerID));
            if (custSnap.exists()) customerData = custSnap.data();
          }

          const reconstructedComponents = (mData.components || []).map(mc => {
            let originalBaseQty = mc.perSet; 
            if (!originalBaseQty && orderData) {
                orderData.boms?.forEach(bom => {
                    const match = bom.components?.find(bc => bc.sku === mc.sku);
                    if (match) originalBaseQty = match.baseQty;
                });
            }
            return { ...mc, perSet: originalBaseQty || 1 };
          });
          
          setDcData({
            firmId: mData.firmId || orderData?.firmId || '',
            challanNo: mData.challanNo || `DC-${mData.mocNo}`,
            dispatchDate: mData.dispatchDate || mData.deliveryDate || new Date().toISOString().split('T')[0],
            deliveryAgent: mData.deliveryAgent || '',
            deliveryMode: mData.deliveryMode || 'Truck',
            coordinator: mData.coordinator || mData.preparedBy || '',
            dispatchedThrough: mData.dispatchedThrough || '',
            poNumber: mData.poNumber || '',
            poDate: mData.poDate || '',
            irnNo: mData.irnNo || '',
            billToCustomer: mData.billToCustomer || customerData?.company_name || mData.customerName || '',
            shipToCustomer: mData.shipToCustomer || orderData?.shippingAddress?.attention || mData.customerName || '',
            billToAddress: mData.billToAddress || formatAddress(orderData?.billingAddress),
            shipToAddress: mData.shipToAddress || mData.shippingAddress || '',
            billToGstin: mData.billToGstin || customerData?.gstin || customerData?.gst_number || '',
            salesperson: mData.salesperson || orderData?.salesPerson || mData.approvedBySalesperson || '',
            orderBy: mData.orderBy || orderData?.billingAddress?.attention || '',
            orderByContact: mData.orderByContact || orderData?.billingAddress?.phone || '',
            sitePerson: mData.sitePerson || orderData?.shippingAddress?.attention || '',
            sitePersonContact: mData.sitePersonContact || orderData?.shippingAddress?.phone || '',
            billToEmail: mData.billToEmail || orderData?.billingAddress?.email || '',
            shipToEmail: mData.shipToEmail || orderData?.shippingAddress?.email || '',
            itemCode: mData.itemCode || '',
            towerHeight: mData.towerHeight || '',
            totalMeter: mData.totalMeter || mData.totalMeterNumber || '',
            receivedByName: mData.receivedByName || '',
            receivedByContact: mData.receivedByContact || '',
            handoverDateTime: mData.handoverDateTime || '',
            vehicleType: mData.vehicleType || '',
            taxInvoiceUrl: mData.taxInvoiceUrl || '',
            ewayBillUrl: mData.ewayBillUrl || '',
            einvoiceUrl: mData.einvoiceUrl || '',
            isDCConfirmed: mData.isDCConfirmed || false,
            approvedByFinance: mData.approvedByFinance || '',
            components: reconstructedComponents
          });
        } else {
          alert("MOC not found");
          navigate(user?.role === 'accounts' ? '/accounts/orders' : '/ops/ready-to-dispatch');
        }
      } catch (error) {
        console.error("Error fetching DC data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, id, navigate, user?.role]);

  // Real-time subscription to dispatches for the timeline
  useEffect(() => {
    if (!moc?.orderId) return;
    const q = query(collection(db, "dispatches"), where("orderId", "==", moc.orderId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dispatchesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDispatches(dispatchesList);
    });
    return () => unsubscribe();
  }, [db, moc?.orderId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDcData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(prev => ({ ...prev, [type]: true }));
    try {
      const path = `dispatch_docs/${id}/${type}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const fieldName = type === 'invoice' ? 'taxInvoiceUrl' : (type === 'eway' ? 'ewayBillUrl' : 'einvoiceUrl');
      setDcData(prev => ({ ...prev, [fieldName]: url }));
      await updateDoc(doc(db, "dispatches", id), { [fieldName]: url });
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleComponentChange = (index, field, value) => {
    const updated = [...dcData.components];
    updated[index][field] = field === 'perSet' || field === 'newQty' ? (parseInt(value) || 0) : value;
    setDcData(prev => ({ ...prev, components: updated }));
  };

  const deleteComponent = (index) => {
    const updated = dcData.components.filter((_, i) => i !== index);
    setDcData(prev => ({ ...prev, components: updated }));
  };

  const addComponent = (product) => {
    const existingIndex = dcData.components.findIndex(c => c.sku === product.sku);
    if (existingIndex > -1) {
      const currentQty = parseInt(dcData.components[existingIndex].newQty || 0);
      handleComponentChange(existingIndex, 'newQty', currentQty + 1);
    } else {
      const newComp = { sku: product.sku, name: product.name, newQty: 1, rentalQty: 0, perSet: 1 };
      setDcData(prev => ({ ...prev, components: [...prev.components, newComp] }));
    }
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const handleSearch = (val) => {
    setSearchTerm(val);
    if (val.length > 1) {
      const matches = bomDatabase.filter(p => p.name.toLowerCase().includes(val.toLowerCase()) || p.sku.toLowerCase().includes(val.toLowerCase())).slice(0, 10);
      setFilteredProducts(matches);
      setShowSuggestions(true);
    } else {
      setFilteredProducts([]);
      setShowSuggestions(false);
    }
  };

  const totals = useMemo(() => {
    return dcData.components.reduce((acc, c) => {
      acc.totalQty += (parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0));
      return acc;
    }, { totalQty: 0 });
  }, [dcData.components]);

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const updates = {
        ...dcData,
        updatedAt: serverTimestamp()
      };

      // If it was on hold, resume it by clearing hold data and status
      if (moc?.dispatchStatus === 'dc_hold') {
          updates.dispatchStatus = 'packed_ready';
          updates.dcHoldReason = '';
      }

      await updateDoc(doc(db, "dispatches", id), updates);

      if (moc?.orderId && moc?.dispatchStatus === 'dc_hold') {
          await updateDoc(doc(db, "orders", moc.orderId), {
              status: 'packed_ready',
              dcHoldReason: '',
              updatedAt: serverTimestamp()
          });
      }

      alert("Draft Saved Successfully. DC Resumed (if it was on hold).");
      setIsEditing(false);
      // Reload local data to reflect status change
      setMoc(prev => ({ ...prev, dispatchStatus: updates.dispatchStatus, dcHoldReason: '' }));
    } catch (e) {
      alert("Error saving: " + e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleHoldSubmit = async () => {
    if (!holdReason.trim()) return alert("Please provide a reason for the hold.");
    setIsSaving(true);
    try {
      const timestamp = serverTimestamp();
      const updates = {
        dispatchStatus: 'dc_hold',
        dcHoldReason: holdReason,
        updatedAt: timestamp
      };

      await updateDoc(doc(db, "dispatches", id), updates);

      if (moc?.orderId) {
        await updateDoc(doc(db, "orders", moc.orderId), { 
            status: 'dc_hold',
            dcHoldReason: holdReason,
            updatedAt: timestamp 
        });
      }

      alert("DC placed on Hold.");
      setShowHoldModal(false);
      setMoc(prev => ({ ...prev, ...updates }));
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDC = async () => {
    if (!window.confirm("Confirm this Delivery Challan? This will move it to the Invoicing queue for Accounts.")) return;
    setIsSaving(true);
    try {
      const timestamp = serverTimestamp();
      await updateDoc(doc(db, "dispatches", id), {
        ...dcData,
        isDCConfirmed: true,
        dispatchStatus: 'invoice_pending',
        dcCreatedAt: timestamp,
        updatedAt: timestamp
      });
      if (dcData.orderId) {
        await updateDoc(doc(db, "orders", dcData.orderId), {
          status: 'invoice_pending',
          updatedAt: timestamp
        });
      }
      alert("Delivery Challan Confirmed! Moved to Accounts for Invoicing.");
      navigate('/ops/ready-to-dispatch');
    } catch (e) {
      alert("Error confirming: " + e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmInvoice = async () => {
    if (!dcData.taxInvoiceUrl) return alert("Please upload the Tax Invoice before confirming.");
    if (!window.confirm("Confirm Invoice and E-way bill details? This will move the DC to Quality Check.")) return;
    
    setIsSaving(true);
    try {
      const timestamp = serverTimestamp();
      await updateDoc(doc(db, "dispatches", id), {
        dispatchStatus: 'invoice_ready',
        documentsReadyAt: timestamp,
        updatedAt: timestamp
      });
      if (moc?.orderId) {
        await updateDoc(doc(db, "orders", moc.orderId), {
          status: 'invoice_ready',
          updatedAt: timestamp
        });
      }
      alert("Invoice Details Confirmed! Moved to Quality Check queue.");
      navigate('/accounts/orders');
    } catch (e) {
      alert("Error: " + e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPDF = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>DC ${dcData.challanNo}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #000; margin: 0; padding: 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 0px; }
            td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 12px; }
            @page { margin: 1cm; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  const selectedFirm = firms.find(f => f.id === dcData.firmId);

  // --- RENDER PREVIEW ---
  const renderPreview = () => (
    <div style={pdfContainerStyle} ref={printRef}>
      {/* SECTION 1: HEADER */}
      <table style={headerTableStyle}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center' }}>
              {selectedFirm?.logo && <img src={selectedFirm.logo} alt="Logo" style={{ maxWidth: '120px' }} />}
            </td>
            <td style={{ ...headerTableCellStyle, width: '50%', fontSize: '12px' }}>
              <strong style={{ fontSize: '16px' }}>{selectedFirm?.name}</strong><br />
              {selectedFirm?.address}<br />
              GSTIN: {selectedFirm?.gstin}<br />
              Email: {selectedFirm?.email} | Tel: {selectedFirm?.tel}
            </td>
            <td style={{ ...headerTableCellStyle, width: '30%', textAlign: 'center', verticalAlign: 'middle' }}>
              <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '900' }}>DELIVERY CHALLAN</h1>
            </td>
          </tr>
        </tbody>
      </table>

      {/* SECTION 2: DOCUMENT & DELIVERY */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>Document Details</td>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>Delivery Details</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}><strong>Challan#:</strong> {dcData.challanNo}</td>
            <td style={headerTableCellStyle}><strong>PO Number:</strong> {dcData.poNumber}</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}><strong>Dispatch Date:</strong> {dcData.dispatchDate}</td>
            <td style={headerTableCellStyle}><strong>PO Date:</strong> {dcData.poDate}</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}><strong>Delivery Agent:</strong> {dcData.deliveryAgent}</td>
            <td style={headerTableCellStyle}><strong>Delivery Mode:</strong> {dcData.deliveryMode} (Type: {dcData.vehicleType || '---'})</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}><strong>Coordinator:</strong> {dcData.coordinator}</td>
            <td style={headerTableCellStyle}><strong>Dispatched Through:</strong> {dcData.dispatchedThrough}</td>
          </tr>
          {dcData.irnNo && (
            <tr><td colSpan="2" style={headerTableCellStyle}><strong>IRN#:</strong> {dcData.irnNo}</td></tr>
          )}
        </tbody>
      </table>

      {/* SECTION 3: BILL TO | SHIP TO */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>Bill to</td>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>Ship to</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}><strong>Customer:</strong> {dcData.billToCustomer}</td>
            <td style={headerTableCellStyle}><strong>Customer:</strong> {dcData.shipToCustomer}</td>
          </tr>
          <tr>
            <td style={{ ...headerTableCellStyle, height: '50px', verticalAlign: 'top' }}><strong>Address:</strong> {dcData.billToAddress}</td>
            <td style={{ ...headerTableCellStyle, height: '50px', verticalAlign: 'top' }}><strong>Address:</strong> {dcData.shipToAddress}</td>
          </tr>
          <tr><td style={headerTableCellStyle}><strong>GSTIN/UIN:</strong> {dcData.billToGstin}</td><td rowSpan={2} style={headerTableCellStyle}></td></tr>
          <tr><td style={headerTableCellStyle}><strong>Salesperson:</strong> {dcData.salesperson}</td></tr>
          <tr>
            <td style={headerTableCellStyle}><strong>Order By:</strong> {dcData.orderBy}<br/><strong>Contact:</strong> {dcData.orderByContact}</td>
            <td style={headerTableCellStyle}><strong>Site Person:</strong> {dcData.sitePerson}<br/><strong>Contact:</strong> {dcData.sitePersonContact}</td>
          </tr>
          <tr><td style={headerTableCellStyle}><strong>Email:</strong> {dcData.billToEmail}</td><td style={headerTableCellStyle}><strong>Email:</strong> {dcData.shipToEmail}</td></tr>
        </tbody>
      </table>

      {/* SECTION 4: ITEM SUMMARY */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '33%' }}><strong>Item code:</strong> {dcData.itemCode}</td>
            <td style={{ ...headerTableCellStyle, width: '33%' }}><strong>Tower Height:</strong> {dcData.towerHeight}</td>
            <td style={{ ...headerTableCellStyle, width: '34%' }}><strong>Total Meter:</strong> {dcData.totalMeter}</td>
          </tr>
        </tbody>
      </table>

      {/* SECTION 5: MATERIAL TABLE */}
      <div style={{ marginTop: '20px' }}>
          <div style={{ backgroundColor: '#f1f5f9', padding: '8px 10px', border: '1px solid #000', borderBottom: 'none', fontWeight: 'bold', fontSize: '14px' }}>MATERIAL BREAKDOWN</div>
          <table style={headerTableStyle}>
              <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}><th style={pdfThStyle}>Sr. No.</th><th style={pdfThStyle}>SKU</th><th style={{...pdfThStyle, textAlign:'left'}}>Item</th><th style={pdfThStyle}>Qty Per Set</th><th style={pdfThStyle}>Total Qty</th></tr>
              </thead>
              <tbody>
                  {dcData.components.map((c, i) => (
                      <tr key={i}>
                          <td style={{...pdfTdStyle, textAlign:'center'}}>{i+1}</td><td style={{...pdfTdStyle, textAlign:'center'}}>{c.sku}</td><td style={pdfTdStyle}>{c.name}</td>
                          <td style={{...pdfTdStyle, textAlign:'center'}}>{c.perSet}</td><td style={{...pdfTdStyle, textAlign:'center', fontWeight:'bold'}}>{parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0)}</td>
                      </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}><td colSpan="4" style={{ ...pdfTdStyle, textAlign: 'right', paddingRight: '10px' }}>TOTAL QUANTITY:</td><td style={{ ...pdfTdStyle, textAlign: 'center' }}>{totals.totalQty}</td></tr>
              </tbody>
          </table>
      </div>
      
      {/* SECTION 6: TERMS & CONDITIONS */}
      <table style={{ ...headerTableStyle, borderTop: 'none', marginTop:'20px' }}>
        <tbody>
          <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}><td style={{ ...headerTableCellStyle, fontSize: '14px' }}>Terms and Conditions</td></tr>
          <tr>
            <td style={{ ...headerTableCellStyle, fontSize: '11px', lineHeight: '1.6' }}>
                1. Have received the material in good condition and satisfied with the quality of same.<br/>
                2. Have read the scaffold safety notes and also the instructions given by the Nexrise India team for proper use of the scaffold.<br/>
                3. Received and carefully read the scaffold safety notes for meticulous compliance at site.<br/>
                4. Have verified and cross checked the material quantity. Any post discrepancy or issue for material condition will not be acceted.
            </td>
          </tr>
        </tbody>
      </table>

      {/* SECTION 7: SIGNATURES */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>
                <strong>Received By (Name):</strong> {dcData.receivedByName || '____________________'}<br/><br/>
                <strong>Contact Number:</strong> {dcData.receivedByContact || '____________________'}<br/><br/>
                <strong>Date & Time of Handover:</strong> {dcData.handoverDateTime || '____________________'}
            </td>
            <td style={{ ...headerTableCellStyle, width: '50%', textAlign: 'center', verticalAlign: 'bottom', height: '120px' }}>
                <strong>Customer seal and Signature</strong>
            </td>
          </tr>
        </tbody>
      </table>

      {/* SECTION 8: INTERNAL PURPOSE ONLY */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
            <td colSpan="5" style={{ ...headerTableCellStyle, textAlign: 'center' }}>Internal Purpose Only</td>
          </tr>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '20%', verticalAlign: 'center',}}><strong>Verified and Checked By:</strong></td>
            <td rowSpan="3" style={{ ...headerTableCellStyle, width: '20%' }}></td>
            <td rowSpan="3" style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center', verticalAlign: 'bottom', paddingBottom: '15px' }}>
                <strong>Seal & Sign (Consignor)</strong>
            </td>
            <td rowSpan="3" style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center', verticalAlign: 'top' }}>
                <strong>Prepared By:</strong><br/>
                <div style={{ height: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src="/Rajesh-Kumar-Sign.png" alt="Sign" style={{ maxHeight: '100px', objectFit: 'contain' }} />
                </div>
                Rajesh Kumar
            </td>
            <td rowSpan="3" style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center', verticalAlign: 'top' }}>
                <strong>Approved By:</strong><br/>
                <div style={{ height: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src="/Ghansyam-Sign.png" alt="Sign" style={{ maxHeight: '100px', objectFit: 'contain' }} />
                </div>
                Ghanshyam Prajapt
            </td>
          </tr>
          <tr><td style={{ ...headerTableCellStyle, verticalAlign: 'center' }}><strong>Time & date:</strong></td></tr>
          <tr><td style={{ ...headerTableCellStyle, verticalAlign: 'center' }}><strong>Sign:</strong></td></tr>
        </tbody>
      </table>

      {(dcData.taxInvoiceUrl || dcData.ewayBillUrl || dcData.einvoiceUrl) && (
        <div style={{ marginTop: '10px', fontSize: '9px', color: '#64748b', borderTop: '1px solid #e2e8f0', paddingTop: '5px' }}>
            <strong>Attached Docs:</strong>
            {dcData.taxInvoiceUrl && <span style={{ marginLeft: '10px' }}>Tax Invoice Attached ✅</span>}
            {dcData.ewayBillUrl && <span style={{ marginLeft: '10px' }}>E-way Bill Attached ✅</span>}
            {dcData.einvoiceUrl && <span style={{ marginLeft: '10px' }}>E-Invoice Attached ✅</span>}
        </div>
      )}
    </div>
  );

  const renderEdit = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={cardStyle}>
            <h3 style={sectionHeaderStyle}><FileText size={20} /> DC Header & Tracking</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                <div><label style={labelStyle}>Firm (Consignor)</label><select name="firmId" value={dcData.firmId} onChange={handleChange} style={inputStyle}><option value="">Select Firm...</option>{firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
                <div><label style={labelStyle}>Challan Number</label><input name="challanNo" value={dcData.challanNo} onChange={handleChange} style={inputStyle} /></div>
                <div><label style={labelStyle}>Dispatch Date</label><input type="date" name="dispatchDate" value={dcData.dispatchDate} onChange={handleChange} style={inputStyle} /></div>
                <div><label style={labelStyle}>IRN Number</label><input name="irnNo" value={dcData.irnNo} onChange={handleChange} style={inputStyle} /></div>
                <div><label style={labelStyle}>Delivery Mode</label><input name="deliveryMode" value={dcData.deliveryMode} onChange={handleChange} style={inputStyle} /></div>
                <div><label style={labelStyle}>Delivery Agent</label><input name="deliveryAgent" value={dcData.deliveryAgent} onChange={handleChange} style={inputStyle} /></div>
                <div><label style={labelStyle}>Dispatched Through</label><input name="dispatchedThrough" value={dcData.dispatchedThrough} onChange={handleChange} style={inputStyle} /></div>
                <div><label style={labelStyle}>Coordinator</label><input name="coordinator" value={dcData.coordinator} onChange={handleChange} style={inputStyle} /></div>
                <div><label style={labelStyle}>PO Number</label><input name="poNumber" value={dcData.poNumber} onChange={handleChange} style={inputStyle} /></div>
                <div><label style={labelStyle}>PO Date</label><input type="date" name="poDate" value={dcData.poDate} onChange={handleChange} style={inputStyle} /></div>
                <div><label style={labelStyle}>Salesperson</label><input name="salesperson" value={dcData.salesperson} onChange={handleChange} style={inputStyle} /></div>
            </div>
        </div>

        <div style={{ ...cardStyle, border: '2px solid #2563eb' }}>
            <h3 style={{ ...sectionHeaderStyle, color: '#2563eb' }}><User size={20} /> Bill To / Ship To Overrides</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                <div>
                    <h4 style={sectionSubHeader}>Billing</h4>
                    <label style={labelStyle}>Customer</label><input name="billToCustomer" value={dcData.billToCustomer} onChange={handleChange} style={inputStyle} />
                    <label style={{...labelStyle, marginTop:'10px'}}>Address</label><textarea name="billToAddress" value={dcData.billToAddress} onChange={handleChange} style={{...inputStyle, minHeight:'60px'}} />
                    <label style={{...labelStyle, marginTop:'10px'}}>GSTIN</label><input name="billToGstin" value={dcData.billToGstin} onChange={handleChange} style={inputStyle} />
                </div>
                <div>
                    <h4 style={sectionSubHeader}>Shipping</h4>
                    <label style={labelStyle}>Customer</label><input name="shipToCustomer" value={dcData.shipToCustomer} onChange={handleChange} style={inputStyle} />
                    <label style={{...labelStyle, marginTop:'10px'}}>Address</label><textarea name="shipToAddress" value={dcData.shipToAddress} onChange={handleChange} style={{...inputStyle, minHeight:'60px'}} />
                </div>
            </div>
        </div>

        <div style={cardStyle}>
            <h3 style={sectionHeaderStyle}><FileText size={20} /> BOM Table:</h3>
            <div style={{ position: 'relative', width: '300px', marginBottom: '15px' }} ref={searchRef}>
                <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#f3f4f6', padding: '8px 12px', borderRadius: '6px' }}>
                    <Search size={16} color="#64748b" />
                    <input type="text" placeholder="Add extra items..." value={searchTerm} onChange={(e) => handleSearch(e.target.value)} onFocus={() => searchTerm.length > 1 && setShowSuggestions(true)} style={{ border: 'none', background: 'transparent', marginLeft: '8px', outline: 'none', width: '100%', fontSize: '13px' }} />
                </div>
                {showSuggestions && (
                    <div style={suggestionsBoxStyle}>
                        {filteredProducts.map(p => (<div key={p.id} onClick={() => addComponent(p)} style={suggestionItemStyle}><strong>{p.sku}</strong> - {p.name}</div>))}
                    </div>
                )}
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                    <thead>
                        <tr style={{ backgroundColor: '#f9fafb' }}><th style={thStyle}>S.No.</th><th style={thStyle}>SKU</th><th style={{...thStyle, textAlign:'left'}}>Item</th><th style={thStyle}>Qty Per Set</th><th style={thStyle}>Total Qty</th><th style={thStyle}>Action</th></tr>
                    </thead>
                    <tbody>
                        {dcData.components.map((c, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={tdStyle}>{idx + 1}</td><td style={tdStyle}>{c.sku}</td><td style={{...tdStyle, textAlign:'left'}}>{c.name}</td>
                                <td style={tdStyle}><input type="number" value={c.perSet || ''} onChange={(e) => handleComponentChange(idx, 'perSet', e.target.value)} style={{ ...inputStyle, width: '80px', textAlign: 'center' }} /></td>
                                <td style={tdStyle}><input type="number" value={parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0)} onChange={(e) => handleComponentChange(idx, 'newQty', e.target.value)} style={{ ...inputStyle, width: '80px', textAlign: 'center', fontWeight: 'bold' }} /></td>
                                <td style={tdStyle}><button onClick={() => deleteComponent(idx)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={16} /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
  );

  const isAccounts = user?.role === 'accounts';
  const isOps = user?.role === 'ops';
  const isDCReady = moc?.dispatchStatus === 'invoice_pending';
  const isDCFinalized = ['invoice_ready', 'dc_confirmed', 'qc_passed', 'dispatched', 'in_transit', 'delivered'].includes(moc?.dispatchStatus);

  if (loading) return <Spinner />;

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      {/* Status Banner */}
      {moc?.dispatchStatus === 'dc_hold' && (
        <div style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '12px', animation: 'fadeIn 0.3s' }}>
          <Clock color="#faad14" size={24} />
          <div>
            <div style={{ fontWeight: 'bold', color: '#856404', fontSize: '16px' }}>Delivery Challan On Hold</div>
            <div style={{ fontSize: '14px', color: '#856404', marginTop: '4px' }}>Reason: {moc.dcHoldReason}</div>
            <div style={{ fontSize: '12px', color: '#b2910b', marginTop: '8px', fontStyle: 'italic' }}>Ops Team: Saving any changes or draft will automatically resume this DC.</div>
          </div>
        </div>
      )}

      <div style={toolbarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate(isAccounts ? '/accounts/orders' : '/ops/ready-to-dispatch')} style={backBtnStyle}><ArrowLeft size={24} /></button>
          <h2 style={{ margin: 0 }}>DC Setup: {moc?.mocNo}</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {/* Ops Controls */}
          {isOps && !dcData.isDCConfirmed && (
            <>
                {!isEditing ? (
                    <>
                        <button onClick={() => setIsEditing(true)} style={editBtnStyle}><Edit2 size={18} /> Edit DC</button>
                        
                        <button onClick={handleConfirmDC} disabled={isSaving} style={confirmBtnStyle}><FileCheck size={18} /> Confirm DC</button>
                        <div style={{ position: 'relative' }} ref={moreDropdownRef}>
                            <button onClick={() => setShowMoreDropdown(!showMoreDropdown)} style={secondaryBtnStyle}>More <ChevronDown size={18}/></button>
                            {showMoreDropdown && (
                                <div style={{ position: 'absolute', top: '100%', right: 0, backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 100, minWidth: '160px', marginTop: '5px' }}>
                                    <button 
                                        onClick={() => { setShowHoldModal(true); setShowMoreDropdown(false); }} 
                                        style={{ width: '100%', padding: '10px 15px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', color: '#f59e0b' }}
                                        onMouseOver={(e) => e.target.style.backgroundColor = '#fffbeb'}
                                        onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                                    >
                                        <Clock size={16} /> Hold DC
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <button onClick={handleSaveDraft} disabled={isSaving} style={editBtnStyle}><Save size={18} /> Save Draft</button>
                        <button onClick={() => setIsEditing(false)} style={secondaryBtnStyle}>Cancel</button>
                    </>
                )}
            </>
          )}

          {/* Accounts Controls */}
          {isAccounts && isDCReady && (
              <button onClick={handleConfirmInvoice} disabled={isSaving} style={confirmBtnStyle}><FileCheck size={18} /> Confirm Invoice Details</button>
          )}

          {/* Indicators */}
          {(dcData.isDCConfirmed || moc?.dispatchStatus === 'dc_hold') && !isAccounts && (
            <div style={{ backgroundColor: isDCFinalized ? '#f0fdf4' : (moc?.dispatchStatus === 'dc_hold' ? '#fffbe6' : '#fffbeb'), padding: '8px 15px', borderRadius: '8px', border: `1px solid ${isDCFinalized ? '#bbf7d0' : (moc?.dispatchStatus === 'dc_hold' ? '#ffe58f' : '#fef3c7')}`, color: isDCFinalized ? '#16a34a' : (moc?.dispatchStatus === 'dc_hold' ? '#856404' : '#d97706'), fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isDCFinalized ? <CheckCircle size={18} /> : <Clock size={18} />} 
                {moc?.dispatchStatus === 'dc_hold' ? 'DC ON HOLD' : (moc?.dispatchStatus === 'invoice_ready' ? 'READY FOR QC' : (isDCFinalized ? 'DC FINALIZED' : 'PENDING ACCOUNTS INVOICE'))}
            </div>
          )}

          {!isEditing && <button onClick={handleExportPDF} style={secondaryBtnStyle}><Printer size={18} /> Export PDF</button>}
        </div>
      </div>

      {/* Main Content Area */}
      {isEditing && isOps && !dcData.isDCConfirmed ? renderEdit() : renderPreview()}

      {/* Accounts Dispatch Documents Section (Separate Card below preview) */}
      {isAccounts && !isDCFinalized && (
          <div style={{ ...cardStyle, marginTop: '20px', border: '2px solid #10b981' }}>
              <h3 style={{ ...sectionHeaderStyle, color: '#10b981' }}><Upload size={20} /> Dispatch Documents Upload</h3>
              <p style={{fontSize: '13px', color: '#64748b', marginBottom: '20px'}}>Please upload the Tax Invoice and other regulatory documents before confirming the DC.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                  {['invoice', 'eway', 'einvoice'].map((type) => (
                      <div key={type}>
                          <label style={labelStyle}>{type === 'invoice' ? 'Tax Invoice' : (type === 'eway' ? 'E-way Bill' : 'E-Invoice')}</label>
                          <input type="file" id={`upload-acc-${type}`} style={{ display: 'none' }} onChange={(e) => handleFileUpload(e, type)} />
                          <label htmlFor={`upload-acc-${type}`} style={{ ...inputStyle, height:'100px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', cursor:'pointer', gap:'8px', backgroundColor: (type === 'invoice' ? dcData.taxInvoiceUrl : (type === 'eway' ? dcData.ewayBillUrl : dcData.einvoiceUrl)) ? '#f0fdf4' : '#fff' }}>
                              {uploading[type] ? <Loader2 className="animate-spin" size={24} /> : (type === 'invoice' ? dcData.taxInvoiceUrl : (type === 'eway' ? dcData.ewayBillUrl : dcData.einvoiceUrl)) ? <FileCheck size={24} color="#10b981" /> : <Upload size={24} color="#64748b" />}
                              <span style={{fontSize:'12px', fontWeight:'bold'}}>{(type === 'invoice' ? dcData.taxInvoiceUrl : (type === 'eway' ? dcData.ewayBillUrl : dcData.einvoiceUrl)) ? 'Uploaded ✅' : 'Choose File'}</span>
                          </label>
                      </div>
                  ))}
              </div>
          </div>
      )}

      {/* Shared Attachments Display */}
      {(dcData.taxInvoiceUrl || dcData.ewayBillUrl || dcData.einvoiceUrl) && (
        <div style={{...cardStyle, marginTop: '20px', maxWidth: '1100px', margin: '20px auto', animation: 'fadeIn 0.3s'}}>
          <h3 style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px'}}>
            <Paperclip size={20} color="#2563eb"/> Document Attachments
          </h3>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '15px'}}>
            {dcData.taxInvoiceUrl && (
              <a href={dcData.taxInvoiceUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={{...attachmentIconStyle, backgroundColor: '#c7d2fe', color: '#4f46e5'}}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>Tax Invoice</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(dcData.taxInvoiceUrl)}</div>
                </div>
                <ExternalLink size={18} color="#6b7280"/>
              </a>
            )}
            {dcData.ewayBillUrl && (
              <a href={dcData.ewayBillUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={{...attachmentIconStyle, backgroundColor: '#dcfce7', color: '#166534'}}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>E-way Bill</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(dcData.ewayBillUrl)}</div>
                </div>
                <ExternalLink size={18} color="#6b7280"/>
              </a>
            )}
            {dcData.einvoiceUrl && (
              <a href={dcData.einvoiceUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                <div style={{...attachmentIconStyle, backgroundColor: '#fef9c3', color: '#854d0e'}}><FileText size={20}/></div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={attachmentLabelStyle}>E-Invoice</div>
                  <div style={attachmentNameStyle}>{getFileNameFromUrl(dcData.einvoiceUrl)}</div>
                </div>
                <ExternalLink size={18} color="#6b7280"/>
              </a>
            )}
          </div>
        </div>
      )}

      {/* HOLD MODAL */}
      {showHoldModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '450px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Clock size={20} /> Place DC on Hold
              </h3>
              <button onClick={() => setShowHoldModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
                Explain why this Delivery Challan is being put on hold.
            </p>
            <textarea 
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                style={{ ...inputStyle, minHeight: '100px', marginBottom: '20px' }}
                placeholder="e.g. Vehicle breakdown, Waiting for customer confirmation, Document mismatch..."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowHoldModal(false)} style={secondaryBtnStyle}>Cancel</button>
              <button onClick={handleHoldSubmit} disabled={isSaving || !holdReason.trim()} style={{ ...confirmBtnStyle, backgroundColor: '#f59e0b', color: 'white' }}>
                {isSaving ? <Loader2 className="animate-spin" size={16} /> : 'Confirm Hold'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* FOOTER TIMELINE */}
      {!loading && moc && (
        <div style={{ 
          marginTop: '40px', 
          maxWidth: '1100px', 
          margin: '50px auto 20px auto',
          animation: 'fadeIn 0.5s ease-in-out'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            color: '#374151', 
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Clock size={18} color="#2563eb" /> Order Timeline
          </h3>
          <HorizontalTimeline orderData={order} dispatchesData={dispatches} />
        </div>
      )}
    </div>
  );
};

const toolbarStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', backgroundColor: 'white', padding: '15px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const cardStyle = { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const sectionHeaderStyle = { fontSize: '18px', fontWeight: 'bold', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', color: '#1e293b' };
const sectionSubHeader = { fontSize: '14px', fontWeight: 'bold', color: '#2563eb', marginBottom: '15px', borderBottom: '1px solid #e2e8f0', paddingBottom: '5px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const confirmBtnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: 'none', backgroundColor: '#059669', color: 'white' };
const editBtnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: 'none', backgroundColor: '#2563eb', color: 'white' };
const secondaryBtnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151' };
const pdfContainerStyle = { backgroundColor: 'white', padding: '40px', border: '1px solid #e2e8f0', minHeight: '1000px', color: '#000' };
const headerTableStyle = { width: '100%', borderCollapse: 'collapse', border: '1px solid #000' };
const headerTableCellStyle = { border: '1px solid #000', padding: '8px', fontSize: '12px', textAlign: 'left' };
const pdfThStyle = { border: '1px solid #000', padding: '6px', fontSize: '11px', textAlign: 'center', fontWeight: 'bold' };
const pdfTdStyle = { border: '1px solid #000', padding: '6px', fontSize: '11px', textAlign: 'left' };
const signLineStyle = { borderTop: '1px solid #000', width: '180px', textAlign: 'center', paddingTop: '10px', fontSize: '12px', fontWeight: 'bold' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle = { padding: '12px 10px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #f3f4f6' };
const tdStyle = { padding: '12px 10px', fontSize: '13px', color: '#374151', textAlign: 'center' };
const suggestionsBoxStyle = { position: 'absolute', top: '100%', left: 0, width: '100%', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '4px', zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' };
const suggestionItemStyle = { padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' };

const attachmentCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  textDecoration: 'none',
  color: '#374151',
  transition: 'all 0.2s ease-in-out',
};

const attachmentIconStyle = {
  padding: '8px',
  backgroundColor: '#e0f2fe',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#0284c7',
};

const attachmentLabelStyle = {
  fontSize: '11px',
  fontWeight: '600',
  color: '#64748b',
  textTransform: 'uppercase',
  marginBottom: '2px',
};

const attachmentNameStyle = {
  fontSize: '13px',
  fontWeight: 'bold',
  color: '#1e293b',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'block',
};

export default DCDetail;
